import React, { useCallback, useEffect, useState } from 'react';
import { Phone, Mail, MapPin, FileText, Merge, Undo2, X, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { clientsApi } from '../lib/api';

const STATUS_LABELS = { pending: 'En attente', confirmed: 'Confirmé', completed: 'Terminé', cancelled: 'Annulé' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

const Contact = ({ c }) => (
  <div className="grid sm:grid-cols-2 gap-3 mt-4" data-testid="client-contact">
    <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-xl text-sm"><Phone className="w-4 h-4 text-blue-700" />{c.phone || '—'}</div>
    <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-xl text-sm truncate"><Mail className="w-4 h-4 text-blue-700" />{c.email || '—'}</div>
    <div className="sm:col-span-2 flex items-center gap-2 p-3 bg-slate-50 rounded-xl text-sm"><MapPin className="w-4 h-4 text-slate-500" />{[c.address, c.city].filter(Boolean).join(', ') || '—'}</div>
  </div>
);

const QuoteList = ({ quotes, labels, onOpenQuote }) => (
  <div className="mt-5" data-testid="client-quotes">
    <p className="text-xs uppercase font-bold text-slate-500 mb-2">Devis ({quotes.length})</p>
    <div className="space-y-2">
      {quotes.map((q) => (
        <div key={q.id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl" data-testid={`client-quote-${q.id}`}>
          <FileText className="w-4 h-4 text-slate-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{q.services.map((s) => labels[s] || s).join(', ')}</p>
            <p className="text-xs text-slate-500">{fmt(q.created_at)} · {STATUS_LABELS[q.status] || q.status} · Réf. {q.id.slice(0, 8)}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => onOpenQuote(q)} data-testid={`open-quote-${q.id}`}>Ouvrir le devis</Button>
        </div>
      ))}
    </div>
  </div>
);

const PeopleBlock = ({ title, people, testid, children }) => people.length > 0 && (
  <div className="mt-5 p-3 rounded-xl border border-amber-200 bg-amber-50/50" data-testid={testid}>
    <p className="text-xs uppercase font-bold text-amber-700 mb-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />{title}</p>
    {people.map((p) => (
      <div key={p.id} className="flex flex-wrap items-center gap-2 py-1.5">
        <span className="flex-1 text-sm text-slate-800">{p.first_name} {p.last_name}
          <span className="text-slate-500"> {[p.emails?.[0], p.phones?.[0]].filter(Boolean).join(' · ')}</span></span>
        {children(p)}
      </div>
    ))}
  </div>
);

export const ClientDetailDialog = ({ clientId, labels, onClose, onOpenQuote, onChanged }) => {
  const [client, setClient] = useState(null);
  const [confirmMerge, setConfirmMerge] = useState(null);

  const load = useCallback(async () => {
    try { setClient((await clientsApi.get(clientId)).data); } catch { toast.error('Fiche client introuvable'); onClose(); }
  }, [clientId, onClose]);

  useEffect(() => { if (clientId) { setClient(null); load(); } }, [clientId, load]);

  const act = async (fn, msg) => {
    try { const res = await fn(); if (res.data?.id) setClient(res.data); else load(); toast.success(msg); onChanged(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Action impossible'); }
  };

  return (
    <Dialog open={!!clientId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-detail-dialog">
        {client && (
          <>
            <DialogHeader>
              <DialogTitle data-testid="client-detail-name">{client.first_name} {client.last_name}</DialogTitle>
              <DialogDescription>Coordonnées de la demande la plus récente · Fiche créée le {fmt(client.created_at)}</DialogDescription>
            </DialogHeader>
            <Contact c={client.contact} />
            <PeopleBlock title="Doublons possibles — à vérifier" people={client.duplicates} testid="client-duplicates">
              {(p) => (
                <>
                  <Button size="sm" variant="outline" className="gap-1" onClick={() => setConfirmMerge(p)} data-testid={`merge-${p.id}`}><Merge className="w-3.5 h-3.5" />Fusionner ici</Button>
                  <Button size="sm" variant="ghost" className="gap-1" onClick={() => act(() => clientsApi.dismiss(client.id, p.id), 'Marqué comme personne différente')} data-testid={`dismiss-${p.id}`}><X className="w-3.5 h-3.5" />Pas un doublon</Button>
                </>
              )}
            </PeopleBlock>
            <PeopleBlock title="Fiches fusionnées dans celle-ci" people={client.merged_clients} testid="client-merged">
              {(p) => (
                <Button size="sm" variant="outline" className="gap-1" onClick={() => act(() => clientsApi.unmerge(client.id, p.id), 'Fusion annulée')} data-testid={`unmerge-${p.id}`}><Undo2 className="w-3.5 h-3.5" />Annuler la fusion</Button>
              )}
            </PeopleBlock>
            <QuoteList quotes={client.quotes} labels={labels} onOpenQuote={onOpenQuote} />
          </>
        )}
      </DialogContent>
      <AlertDialog open={!!confirmMerge} onOpenChange={(o) => !o && setConfirmMerge(null)}>
        <AlertDialogContent data-testid="merge-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Fusionner ces deux fiches ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les devis de {confirmMerge?.first_name} {confirmMerge?.last_name} seront rattachés à {client?.first_name} {client?.last_name}.
              Aucun devis n'est modifié et la fusion peut être annulée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="merge-cancel-button">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="merge-confirm-button"
              onClick={() => { const p = confirmMerge; setConfirmMerge(null); act(() => clientsApi.merge(client.id, p.id), 'Fiches fusionnées'); }}>
              Fusionner
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
